/**
 * Wire types for the Jira-like /api/v2 surface.
 *
 * THE ONE IDEA TO HOLD ON TO: a v2 "issue" and a v1 "task" are the same row in
 * the `tasks` table. `Issue` therefore extends the frozen `Task` type rather
 * than replacing it — an edit made here is immediately visible to the iPad app
 * and to the MCP server.
 *
 * Two shape rules the server guarantees and this file mirrors:
 *  - v2 list endpoints use the cursor envelope `{data, next_cursor, total}`,
 *    NOT the Laravel paginate envelope that `/api/tasks` still returns.
 *  - collection endpoints that are not paginated answer `{data: [...]}`.
 */

import type {
  BoardColumn,
  DateString,
  IssueType,
  Label,
  Project,
  PublicUser,
  Role,
  Sprint,
  SprintStatus,
  Task,
  TaskPriority,
  TaskStatus,
  Timestamp,
  User,
  UUID,
} from '@/types/api'

// Re-exported so the board and backlog features have one import site for the
// whole domain vocabulary rather than reaching into two modules.
export type {
  BoardColumn,
  DateString,
  IssueType,
  Label,
  Project,
  PublicUser,
  Role,
  Sprint,
  SprintStatus,
  TaskPriority,
  TaskStatus,
  Timestamp,
  User,
  UUID,
}

/** The v2 projection of a task row. */
export interface Issue extends Task {
  organization_id?: UUID
  reporter?: PublicUser
  resolved_at?: Timestamp | null
}

/** `GET /issues/{key}` — the row plus the relations the detail view needs. */
export interface IssueDetail extends Issue {
  parent: Issue | null
  subtasks: Issue[]
  comment_count: number
  watching: boolean
  column: BoardColumn | null
}

/** `GET /issues` — the cursor envelope. */
export interface IssueListResponse {
  data: Issue[]
  next_cursor: string | null
  total: number
}

export interface BoardColumnView extends BoardColumn {
  issues: Issue[]
  count: number
  points: number
}

/* ---------------------------------------------------------------------------
 * The column registry
 *
 * A board column is a web-only workflow state layered over ONE of the three
 * legacy statuses the shipped iPad app and the MCP server understand. That
 * mapping is `legacy_status`, and it is not an implementation detail the UI can
 * hide: the settings screen shows it, because it is what those clients see.
 * ------------------------------------------------------------------------ */

/** A column as the settings screen needs it: the row plus its issue count. */
export interface ColumnRow extends BoardColumn {
  /** Every issue in the column, epics included. What a delete would move. */
  issue_count: number
  /** `legacy_status` spelled for a person, from the server. */
  status_label: string
}

/** One legacy status, and what the board currently does with it. */
export interface ColumnStatusInfo {
  status: TaskStatus
  label: string
  /** How many columns map to this status. One means the last one: undeletable. */
  columns: number
  /** Where a bare status write from the iPad lands. */
  default_column_id?: UUID
}

/**
 * Every column endpoint answers this, reads and writes alike — a write returns
 * the WHOLE set because column edits cascade (a delete re-homes issues and can
 * promote a new default), so patching one row into a cache would be wrong.
 */
export interface ColumnsResponse {
  data: ColumnRow[]
  statuses: ColumnStatusInfo[]
  /** False when the plan has no custom columns: renames stay allowed, adds do not. */
  can_add_column: boolean
  plan?: string
  /** The row the write touched. Absent on a read. */
  column?: ColumnRow
}

export interface ColumnCreateInput {
  name: string
  legacy_status: TaskStatus
  /** Null or omitted means no limit. */
  wip_limit?: number | null
  is_default_for_status?: boolean
}

export interface ColumnPatchInput {
  name?: string
  legacy_status?: TaskStatus
  /** Explicit null clears the limit; omitting it leaves it alone. */
  wip_limit?: number | null
  is_default_for_status?: boolean
}

/** `GET /projects/{key}/board`. */
export interface BoardResponse {
  project: Project | null
  sprint: Sprint | null
  scope: string
  columns: BoardColumnView[]
  total: number
}

export interface BacklogGroup {
  sprint: Sprint | null
  issues: Issue[]
  count: number
  points: number
}

/** `GET /projects/{key}/backlog`. */
export interface BacklogResponse {
  project: Project | null
  sprints: BacklogGroup[]
  backlog: BacklogGroup
}

export interface SprintView extends Sprint {
  issue_count: number
  done_count: number
  points: number
  completed_story_points: number
  remaining_count: number
}

export type AuthorKind = 'user' | 'agent' | 'system'

export interface Comment {
  id: UUID
  organization_id: UUID
  task_id: UUID
  author_id: number | null
  author_kind: AuthorKind
  body: string
  /** User ids, as strings — the column is the same JSON array `tags` uses. */
  mentions: string[]
  edited_at: Timestamp | null
  deleted_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  author?: PublicUser
  mentioned_user_ids: number[]
  mentioned_users: PublicUser[]
}

export interface ActivityChange {
  from?: unknown
  to?: unknown
}

export interface ActivityEntry {
  id: number
  organization_id: UUID
  project_id: UUID | null
  task_id: UUID | null
  actor_user_id: number | null
  actor_kind: string
  actor_label: string | null
  action: string
  changes: Record<string, unknown> | null
  created_at: Timestamp
  actor?: PublicUser
}

export interface ActivityResponse {
  data: ActivityEntry[]
  next_before: number | null
}

export interface LabelView extends Label {
  issue_count: number
}

export interface MemberRow {
  organization_id: UUID
  user_id: number
  role: Role
  is_default_org: boolean
  joined_at: Timestamp | null
  user?: PublicUser
}

export interface MembersResponse {
  data: MemberRow[]
  total: number
  role: Role
  capabilities: string[]
  seats: number
}

export interface OrganizationSummary {
  id: UUID
  slug: string
  name: string
  type: 'personal' | 'team'
  role: Role
  plan: string
  is_default: boolean
  /**
   * True when THIS workspace has connected its own Claude account.
   *
   * The credential is per workspace — there is no server API key — so the
   * board reads this rather than a deployment-wide flag. Optional so a server
   * older than the field reads as "not connected" instead of crashing.
   */
  ai_connected?: boolean
}

/**
 * Deployment capabilities, reported on the session so the UI knows what to
 * RENDER. They are not permissions — the server still authorises every call.
 */
export interface SessionFeatures {
  /**
   * True when the caller's ACTIVE workspace has connected a Claude account.
   *
   * Prefer `OrganizationSummary.ai_connected` for the workspace in the URL:
   * this one only ever describes the active one, and the app routes by slug.
   */
  ai_issue_generation: boolean
  /** The deployment runs the Whisper engine for server-side dictation. */
  voice_transcription?: boolean
}

/** `GET /api/v2/auth/session`. */
export interface SessionResponse {
  user: User
  /** Empty when the caller authenticated with a bearer token. */
  csrf_token: string
  organizations: OrganizationSummary[]
  active_organization_id: UUID | null
  /** Absent on a server older than the capability flag; treat as all-false. */
  features?: SessionFeatures
}

export interface BulkResponse {
  updated: number
  deleted: number
  issue_keys: string[]
}

/** Body of `POST /issues/{key}/move` — the drag & drop endpoint. */
export interface MoveIssueInput {
  column_id?: UUID
  sprint_id?: UUID | null
  status?: TaskStatus
  /** The issue the moved card lands *below*. */
  after_id?: string
  /** The issue the moved card lands *above*. */
  before_id?: string
}

export interface IssueWriteInput {
  title?: string
  description?: string
  type?: IssueType
  status?: TaskStatus
  priority?: TaskPriority
  column_id?: UUID
  sprint_id?: UUID | null
  parent_id?: UUID | null
  assignee_id?: number | null
  story_points?: number | null
  due_date?: DateString | null
  labels?: string[]
}

export interface BulkSetInput {
  status?: TaskStatus
  priority?: TaskPriority
  sprint_id?: UUID | null
  assignee_id?: number | null
  story_points?: number | null
  due_date?: DateString | null
  labels?: string[]
}

export interface SprintWriteInput {
  name?: string
  goal?: string
  start_date?: DateString | null
  end_date?: DateString | null
  status?: SprintStatus
}

/** Board scope: the active sprint, the backlog, everything, or one sprint id. */
export type BoardScope = 'active' | 'backlog' | 'all' | (string & {})

/** An issue can always be addressed by its key (TSK-12) or by its UUID. */
export function issueRef(issue: Pick<Issue, 'id' | 'issue_key'>): string {
  return issue.issue_key ?? issue.id
}

/** What to print in a badge for an issue that has no key allocated yet. */
export function issueLabel(issue: Pick<Issue, 'id' | 'issue_key'>): string {
  return issue.issue_key ?? `#${issue.id.slice(0, 6)}`
}
